import {
  DEFAULT_FIELD_SELECTION,
  FIELD_ASSET_MANIFEST,
  FIELD_FAMILIES,
  FIELD_FAMILY_LABEL_DEFAULT,
  FIELD_OPTIONS,
  FIELD_VARIANT_LABEL_DEFAULT,
  type FieldFamily,
  type FieldSelection,
  type FieldVariant,
} from './fieldManifest';

export {
  DEFAULT_FIELD_SELECTION,
  FIELD_FAMILIES,
  FIELD_FAMILY_LABEL_DEFAULT,
  FIELD_FAMILY_MANIFEST,
  FIELD_MANIFEST,
  FIELD_OPTIONS,
  FIELD_VARIANT_LABEL_DEFAULT,
  FIELD_VARIANT_LABELS,
} from './fieldManifest';
export type { FieldAssetKey, FieldFamily, FieldOption, FieldSelection, FieldVariant } from './fieldManifest';

export interface FieldGeometry {
  widthMm: number;
  heightMm: number;
  widthCm: number;
  heightCm: number;
  origin: 'top-left';
}

const FIELD_FAMILY_SET = new Set<FieldFamily>(FIELD_FAMILIES.map((family) => family.value));
const FIELD_LABEL_SEPARATOR = ' · ';
const FIELD_FALLBACK_LABEL = 'Field';

export interface FieldLabelDescriptor {
  key: string;
  defaultValue: string;
}

export const createDefaultFieldSelection = (): FieldSelection => ({
  ...DEFAULT_FIELD_SELECTION,
});

/** Returns an i18next key (pass through `t()` for display). */
export const getFieldFamilyLabel = (family: FieldFamily) => FIELD_FAMILIES.find((option) => option.value === family)?.label ?? 'field.fallback';

export const getFieldFamilyLabelDefault = (family: FieldFamily) => FIELD_FAMILY_LABEL_DEFAULT[family] ?? FIELD_FALLBACK_LABEL;

export const getOrderedFieldFamilies = () => FIELD_FAMILIES;

export const getFieldOptionsForFamily = (family: FieldFamily) => FIELD_OPTIONS.filter((option) => option.family === family);

export const getDefaultFieldSelectionForFamily = (family: FieldFamily): FieldSelection => {
  const option = getFieldOptionsForFamily(family)[0] ?? FIELD_OPTIONS[0];

  return {
    family: option.family,
    variant: option.variant,
    accessoriesEnabled: DEFAULT_FIELD_SELECTION.accessoriesEnabled,
  };
};

export const getFieldOption = (selection: Pick<FieldSelection, 'family' | 'variant'>) =>
  FIELD_OPTIONS.find((option) => option.family === selection.family && option.variant === selection.variant);

export const isFieldSelection = (value: unknown): value is FieldSelection => {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as {
    family?: unknown;
    variant?: unknown;
    accessoriesEnabled?: unknown;
  };

  if (!FIELD_FAMILY_SET.has(candidate.family as FieldFamily)) {
    return false;
  }

  return (
    typeof candidate.accessoriesEnabled === 'boolean' &&
    !!getFieldOption({ family: candidate.family as FieldFamily, variant: candidate.variant as FieldVariant })
  );
};

export const normalizeFieldSelection = (value: unknown): FieldSelection => {
  if (isFieldSelection(value)) {
    return {
      family: value.family,
      variant: value.variant,
      accessoriesEnabled: value.accessoriesEnabled,
    };
  }

  return createDefaultFieldSelection();
};

/** Returns an i18next key (pass through `t()` for display). */
export const getFieldVariantLabel = (selection: Pick<FieldSelection, 'family' | 'variant'>) => getFieldOption(selection)?.label ?? 'field.fallback';

export const getFieldVariantLabelDefault = (selection: Pick<FieldSelection, 'family' | 'variant'>) => {
  const option = getFieldOption(selection);
  return option ? FIELD_VARIANT_LABEL_DEFAULT[option.variant] : FIELD_FALLBACK_LABEL;
};

export const getFieldFamilyLabelDescriptor = (family: FieldFamily): FieldLabelDescriptor => ({
  key: getFieldFamilyLabel(family),
  defaultValue: getFieldFamilyLabelDefault(family),
});

export const getFieldVariantLabelDescriptor = (selection: Pick<FieldSelection, 'family' | 'variant'>): FieldLabelDescriptor => ({
  key: getFieldVariantLabel(selection),
  defaultValue: getFieldVariantLabelDefault(selection),
});

export const formatFieldLabel = (familyLabel: string, variantLabel: string) => `${familyLabel}${FIELD_LABEL_SEPARATOR}${variantLabel}`;

export const getFieldLabelDefault = (selection: Pick<FieldSelection, 'family' | 'variant'>) =>
  formatFieldLabel(getFieldFamilyLabelDefault(selection.family), getFieldVariantLabelDefault(selection));

export const getFieldSvgAssetKey = (selection: FieldSelection) => getFieldOption(selection)?.assetKey ?? FIELD_OPTIONS[0].assetKey;

export const getFieldSvgSource = (selection: FieldSelection) => {
  const normalizedSelection = normalizeFieldSelection(selection);
  const assetKey = getFieldSvgAssetKey(normalizedSelection);
  const variants = FIELD_ASSET_MANIFEST[assetKey];

  return normalizedSelection.accessoriesEnabled ? variants.withAccessories : variants.withoutAccessories;
};

const fieldSvgDataUriCache = new Map<string, string>();

export const getFieldSvgDataUri = (selection: FieldSelection) => {
  const svgSource = getFieldSvgSource(selection);
  const cached = fieldSvgDataUriCache.get(svgSource);
  if (cached) {
    return cached;
  }

  const dataUri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgSource)}`;
  fieldSvgDataUriCache.set(svgSource, dataUri);
  return dataUri;
};

const convertMmToCm = (millimeters: number) => Math.round((millimeters / 10) * 1000) / 1000;

const parseSvgDimension = (value: string) => {
  const match = value.match(/-?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : Number.NaN;
};

export const extractGeometryFromSvgSource = (svgSource: string): Pick<FieldGeometry, 'widthMm' | 'heightMm'> => {
  const viewBoxMatch = svgSource.match(/viewBox=["']([^"']+)["']/i);
  if (viewBoxMatch) {
    const parts = viewBoxMatch[1]
      .trim()
      .split(/[\s,]+/)
      .map(Number);

    if (parts.length === 4 && Number.isFinite(parts[2]) && Number.isFinite(parts[3]) && parts[2] > 0 && parts[3] > 0) {
      return { widthMm: parts[2], heightMm: parts[3] };
    }
  }

  const widthMatch = svgSource.match(/\bwidth=["']([^"']+)["']/i);
  const heightMatch = svgSource.match(/\bheight=["']([^"']+)["']/i);

  if (widthMatch && heightMatch) {
    const widthMm = parseSvgDimension(widthMatch[1]);
    const heightMm = parseSvgDimension(heightMatch[1]);

    if (Number.isFinite(widthMm) && Number.isFinite(heightMm) && widthMm > 0 && heightMm > 0) {
      return { widthMm, heightMm };
    }
  }

  throw new Error('Unable to derive field geometry from selected SVG source');
};

export const getFieldGeometry = (selection: FieldSelection): FieldGeometry => {
  const svgSource = getFieldSvgSource(selection);
  const dimensions = extractGeometryFromSvgSource(svgSource);

  return {
    widthMm: dimensions.widthMm,
    heightMm: dimensions.heightMm,
    widthCm: convertMmToCm(dimensions.widthMm),
    heightCm: convertMmToCm(dimensions.heightMm),
    origin: 'top-left',
  };
};

export const clampCoordinateToBounds = (value: number, max: number) => Math.max(0, Math.min(max, Math.round(value * 10) / 10));
